import { Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import 'swiper/css';
import { EffectCoverflow, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { GameChatRoomCard } from '~/components/Chat';
import { Icon } from '~/components/atomic';
import { FullScreenLoading, HelmetHead } from '~/components/common';
import { Header } from '~/components/common/Header';
import { useGetGameDetailQuery } from '~/features/missionGame';
import useGetGameChatroomListQuery from '~/features/missionGame/useGetMissionGameChatroomsQuery';
import { useWebSocket } from '~/hooks';
import { MessageResponse } from '~/types';
import { PAGE_ROUTE } from '~/constants';
import { breakpoint, fontStyle } from '~/styles/themes';
import { useUserStore } from '~/store';
import MissionBottomSheet from '../../components/common/BottomSheet/MissionAnswer/MissionBottomSheet';
import { ButtonContainer } from './main';
import { Button } from './main';

export default function MissionGamePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMissionCompleted } = location.state;
  const { id: gameId } = useParams();

  const email = useUserStore((state) => state.email);
  const [isGameOver, setIsGameOver] = useState(false);
  const [gameOverModalVisible, setGameOverModalVisible] = useState(false);
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [socketConnectSuccess, setSocketConnectSuccess] = useState(false);

  const removeMissionCompleteState = () => {
    navigate(location.pathname, {
      state: {
        ...location.state, // 기존 상태를 복사
        isMissionCompleted: false,
      },
    });
  };

  useWebSocket({
    chatType: 'mission',
    fetchDone: true,
    email,
    setMessages,
    socketConnectSuccess,
    setSocketConnectSuccess,
  });

  useEffect(() => {
    if (!isMissionCompleted) return;
    setIsGameOver(isMissionCompleted);
  }, [isMissionCompleted]);

  useEffect(() => {
    if (isGameOver) {
      requestAnimationFrame(() => setGameOverModalVisible(true));
    } else {
      setGameOverModalVisible(false);
    }
  }, [isGameOver]);

  const GameChatRoomRouteLayout = () => {
    const { data: gameDetails } = useGetGameDetailQuery({
      game_id: Number(gameId),
    });

    const { data: gameChatroomList } = useGetGameChatroomListQuery({
      game_id: Number(gameId),
    });

    return (
      <>
        <ImageBackground src={gameDetails?.thumbnail_file_path} />
        <InnerContainer>
          <WhereToGoText>Where to go?</WhereToGoText>
          <Swiper
            effect={'coverflow'}
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={'auto'}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 100,
              modifier: 2.5,
              slideShadows: false,
            }}
            spaceBetween={50}
            modules={[EffectCoverflow, Pagination]}
            style={{
              width: '100%',
              margin: '0',
              display: 'flex',
              justifyContent: 'center',
            }}
            className='mySwiper'
          >
            {gameChatroomList.map((el, idx) => (
              <SwiperSlide
                key={idx}
                style={{
                  width: 245,
                  borderRadius: '18px',
                }}
              >
                <GameChatRoomCard
                  chatRoomInfo={el}
                  gameId={gameDetails.game_id}
                  gameTitle={gameDetails.title}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {/* 미션 바텀시트 */}
          <MissionBottomSheet
            missions={gameDetails?.questions || []}
            gameId={String(gameDetails?.game_id)}
            gameTitle={gameDetails?.title}
          />
        </InnerContainer>
      </>
    );
  };

  const GameOverLayout = () => {
    return (
      <GameOverContainer visible={gameOverModalVisible}>
        <GameOverModal>
          <GameOverText>GAME OVER</GameOverText>
          Thanks for playing
          <ButtonContainer>
            <Button
              text='Main Menu'
              type='light'
              width={160}
              handleClick={() =>
                navigate(`${PAGE_ROUTE.MISSION_GAME}/main/${gameId}`)
              }
            />
            <Button
              text='Retry'
              type='dark'
              handleClick={() => {
                setIsGameOver(false);
                removeMissionCompleteState();
              }}
              width={160}
            />
          </ButtonContainer>
        </GameOverModal>
      </GameOverContainer>
    );
  };

  return (
    <Suspense fallback={<FullScreenLoading />}>
      <HelmetHead title='Mission Game Chat' />
      <Header.Root transparentBg style={{ paddingLeft: 12 }}>
        <Header.Section gap={14}>
          <RoundedButtonBox onClick={() => navigate(-1)}>
            <Icon iconName='ArrowLeft' />
          </RoundedButtonBox>
          <Header.Title style={{ fontWeight: 700 }}>
            Find Clues in Chats
          </Header.Title>
        </Header.Section>
      </Header.Root>

      <FullScreenContainer>
        {isGameOver && <GameOverLayout />}
        <GameChatRoomRouteLayout />
      </FullScreenContainer>
    </Suspense>
  );
}

const FullScreenContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: ${breakpoint.mobile_large};
  height: 100%;
  margin: 0 auto;
  overflow: hidden;
`;

const ImageBackground = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(10px);
`;

const InnerContainer = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin-top: -12px;
  ${({ theme }) => css`
    background-color: ${theme.backgroundColor.bg_dark_4_alpha};
  `};
`;

const WhereToGoText = styled.div`
  margin-right: 140px;
  ${({ theme }) => css`
    color: ${theme.content.content_light_3};
  `};
  margin-bottom: 13px;
  font-size: 16px;
`;

const GameOverContainer = styled.div<{ visible: boolean }>`
  display: flex;
  position: absolute;
  z-index: 20;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  transition: opacity 1s ease-in-out;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  background-color: ${({ theme }) => theme.content.content_dark_2_alpha};
  inset: 0;
`;

const GameOverModal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 327px;
  height: 327px;
  padding-top: 10px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  background-color: ${({ theme }) => theme.backgroundColor.bg_dark_4_alpha};
  box-shadow: 0px 0px 64px rgba(61, 241, 243, 0.3);
  backdrop-filter: blur(20px);
  gap: 28px;
`;

const GameOverText = styled.h2`
  ${({ theme }) => css`
    ${fontStyle.title.section28_bold}
  `};
`;

export const RoundedButtonBox = styled.div`
  ${({ theme }) => css`
    display: flex;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background-color: ${theme.backgroundColor.bg_dark_5_alpha};
    cursor: pointer;
  `}
`;
